import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import Proptypes from 'prop-types'
import _ from 'lodash'

import API from 'api'
import * as Check from 'validations'
import AppLogic from 'containers/App/logic'
import ToasterLogic from 'containers/Toaster/logic'
import ServiceAreaLogic from 'screens/ServiceArea/logic'
import ServiceAreaRequestLogic from 'screens/ServiceAreaRequest/logic'

import { DEFAULT_VALUES, VALIDATIONS } from './utils'
import {
	scrollToFirstFormError,
	mapAPIFormStateToForm,
	sortedAlphabetically,
	getErrorMessagesAndScroll
} from 'utils'

export default kea({
	path: () => ['scenes', 'containers', 'SAR_SummaryDescription'],

	connect: { 
		props: [
			AppLogic, [
				'user'
			],
			ServiceAreaLogic, [
				'serviceAreaProcessId'
			],
			ServiceAreaRequestLogic, [
				'current'
			]
		],
		actions: [
			ToasterLogic, [
				'show as showToaster'
			],
			ServiceAreaRequestLogic, [
				'setName',
				'setStatus',
				'setCurrent'
			]
		]
	},

	actions: () => ({
		fetchForm: () => true,
		setForm: form => ({ form }),
		changeForm: field => ({ field }),
		unsetDirty: () => true,
    
		patchForm: action => action,
		setDistricts: districts => ({ districts }),
		setMunicipalities: municipalities => ({ municipalities }),

		fetchDistrictsAndMunicipalities: () => true,
		getDataToPrefillFields: () => true,
		error: errors => ({ errors }),
		loading: () => true,
		loaded: () => true,
		reset: () => true
	}),

	reducers: ({ actions }) => ({

		form: [_.cloneDeep(DEFAULT_VALUES), Proptypes.object, {
			[actions.setForm]: (_, payload) => payload.form,
			[actions.changeForm]: (state, payload) =>
				Check.setAndCheckValidation(
					state,
					payload,
					VALIDATIONS
				),
			[actions.reset]: () => _.cloneDeep(DEFAULT_VALUES)
		}],

		dirty: [false, Proptypes.bool, {
			[actions.changeForm]: () => true,
			[actions.reset]: () => false,
			[actions.unsetDirty]: () => false,
		}],

		districts: [[], Proptypes.array, {
			[actions.setDistricts]: (state, payload) => payload.districts,
			[actions.reset]: () => []
		}],

		municipalities: [[], Proptypes.array, {
			[actions.setMunicipalities]:
        (state, payload) => payload.municipalities,
			[actions.reset]: () => []
		}],

		loading: [true, Proptypes.bool, {
			[actions.patchForm]: () => true,
			[actions.loading]: () => true,
			[actions.setForm]: () => false,
			[actions.loaded]: () => false, 
			[actions.error]: () => false,
			[actions.reset]: () => false
		}],

		errors: [[], Proptypes.array, {
			[actions.error]: (state, payload) => payload.errors,
			[actions.patchForm]: () => [],
			[actions.reset]: () => []
		}]

	}),

	start: function * () {
		const { fetchForm, fetchDistrictsAndMunicipalities } = this.actions
		yield put(fetchDistrictsAndMunicipalities())
		yield put(fetchForm())
	},

	takeLatest: ({ actions, workers }) => ({
		[actions.saveForm]: workers.patchForm,
		[actions.patchForm]: workers.patchForm,
		[actions.fetchForm]: workers.fetchForm,
		[actions.getDataToPrefillFields]: workers.getDataToPrefillFields,
		[actions.fetchDistrictsAndMunicipalities]:
      workers.fetchDistrictsAndMunicipalities,
	}),

	workers: {

		* fetchForm () { 
			const { setForm, showToaster, loaded } = this.actions
			const { t } = this.props 
			const form = yield this.get('form')
			const id = yield this.get('serviceAreaProcessId')

			try {
				let data = yield call(API.Areas.steps.getSummary, id)

				if (data.municipality) {
					let newData = {}
					newData.municipality = data.municipality.id

					if (data.municipality.district) {
						newData.district = data.municipality.district.id
					}

					data = { ...data, ...newData }
				}

				const gpsCoordinates = data.gpsCoordinates
				if (gpsCoordinates) {
					// eslint-disable-next-line max-len
					data.gpsCoordinates = gpsCoordinates.latitude + ',' + gpsCoordinates.longitude
				}
				
				const newForm = yield call(
					mapAPIFormStateToForm,
					data,
					_.cloneDeep(form)
				)
				if (!newForm.district) newForm.district = { value: '' }
				
				if (newForm.gpsCoordinates && !newForm.gpsCoordinates.value) {
					newForm.gpsCoordinates = { value: '' }
				}
				
				yield put(setForm(newForm))

			} catch (err) {
				console.log(err)
				yield put(loaded())
				yield put(showToaster(
					'error',
					t('common.error'),
					t('error.error_fetch')
				))
			}

		},

		* fetchDistrictsAndMunicipalities () {
			const {
				setDistricts,
				setMunicipalities,
				showToaster
			} = this.actions
			const { t } = this.props

			try {
				const params = { limit: 'max' }
				const { results } = yield call(
					API.Municipalities.get,
					{ params }
				)
  
				const districts = []
				const map = new Map()
				let bulkedDistricts = results.map((result) => result.district)
  
				for (const item of bulkedDistricts) {
					if (!map.has(item.id)) {
						map.set(item.id, true)
						districts.push({
							id: item.id,
							name: item.name
						})
					}
				}
  
				yield put(setDistricts(districts))
				yield put(setMunicipalities(sortedAlphabetically(
					results,
					'name'
				)))
      
			} catch (err) {
				yield put(showToaster(
					'error',
					t('common.error'),
					t('error.error_fetch')
				))
			}
		},

		* getDataToPrefillFields () {
			const {
				changeForm,
				showToaster
			} = this.actions

			const { t } = this.props
			const form = yield this.get('form')
			const postalCodeValid = form.postalCode.valid
			const input = form.postalCode.value

			if (postalCodeValid) {
				try {
					const params = { code: input }
					const { results } = yield call(
						API.Municipalities.byPostalCode,
						{ params }
					) 
          
					// Set the district, municipality and locality
					const district = !!results.length &&
            results[0].municipality.district.id
					const municipality = !!results.length &&
            results[0].municipality.id
					const locality = !!results.length &&
            results[0].localities[0].name

					if (district) yield put(changeForm({ district }))
					if (municipality) yield put(changeForm({ municipality }))
					if (locality) yield put(changeForm({ locality }))

				} catch (er) {
					console.log(er)
					yield put(showToaster(
						'error',
						t('common.error'),
						t('error.error_fetch')
					))
				}
			}
		},

		* patchForm (actionPayload) {
			const {
				setName,
				showToaster,
				setStatus,
				loaded,
				error,
				setCurrent,
				setForm,
				unsetDirty,
			} = this.actions
			
			const { payload } = actionPayload
			const { t } = this.props			
			const form = yield this.get('form')
			const validation = Check.checkValidation(form, VALIDATIONS)
			const dirty = yield this.get('dirty')

			if (dirty && validation.invalid) {
				// try to scroll to first form field error
				scrollToFirstFormError(validation.form)
				yield put(error([]))
				return false
			}

			if (!dirty && validation.invalid) {
				// try to scroll to first form field error
				scrollToFirstFormError(validation.form)
				yield put(setForm(validation.form))
				yield put(error([]))
				return false
			}
			
			const params = _.mapValues(form, ({ value }) => value)
			const coordinates = params.gpsCoordinates.split(',')
			params.gpsCoordinates = {
				latitude: coordinates[0],
				longitude: coordinates[1]
			}
			
			const serviceAreaProcessId = yield this.get('serviceAreaProcessId')
			const current = yield this.get('current')
      
			if (!dirty) {
				yield put(loaded())
				
				switch (payload) {
					case 'next':
						yield put(setCurrent(current + 1))
						break
					case 'previous':
						yield put(setCurrent(current + 1))
						break
					default:
						break
				}
				
				return
			} 

			try {
				yield put(setName(params.name))
				const response = yield call(
					API.Areas.steps.updateSummary,
					serviceAreaProcessId,
					params
				)
				
				yield put(showToaster(
					'success',
					t('common.success'),
					t('error.success_save_data')
				))
				
				yield put(setStatus('summary_description', response.status))
				yield put(loaded())
				yield put(unsetDirty())
				
				switch (payload) {
					case 'next':
						yield put(setCurrent(current + 1))
						break
					case 'previous':
						yield put(setCurrent(current + 1))
						break
					default:
						break
				}

			} catch (err) {
        
				if (err.response) {
					const errors = getErrorMessagesAndScroll(
						err.response.data,
						form
					)
					yield put(error(errors))
				}

				yield put(showToaster(
					'error',
					t('common.error'),
					t('error.error_save_data')
				))
			}
		},
	}
})
